import React, { useState, useEffect } from 'react';
import { Search, Users, Bell, TrendingUp, MapPin, Calendar, ArrowLeft, Filter, ExternalLink, Clock, Flag, Info, Newspaper, Tag, Moon, Sun, FileText } from 'lucide-react';
import Bills from './Bills';
import { buildApiUrl } from './config';

// Add font import and custom scrollbar styles
const fontStyle = document.createElement('style');
fontStyle.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  /* Custom Scrollbar Styles - Blue Red Gradient */
  
  /* WebKit browsers (Chrome, Safari, Edge) */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    -webkit-appearance: none;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 10px;
    border: none;
  }

  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: content-box;
    -webkit-background-clip: content-box;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    -webkit-border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2563eb 0%, #dc2626 100%);
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  /* Safari specific overrides */
  @supports (-webkit-backdrop-filter: none) {
    ::-webkit-scrollbar {
      width: 8px !important;
      height: 8px !important;
    }
    
    ::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%) !important;
      border-radius: 10px !important;
      border: 2px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
    }
    
    ::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(135deg, #2563eb 0%, #dc2626 100%) !important;
    }
  }

  /* Dark mode scrollbar */
  .dark ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #60a5fa 0%, #f87171 100%);
  }

  .dark ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
  }

  /* Firefox scrollbar */
  * {
    scrollbar-width: thin;
    scrollbar-color: #3b82f6 transparent;
  }

  .dark * {
    scrollbar-color: #60a5fa transparent;
  }

  /* Custom scrollbar for specific elements */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    -webkit-appearance: none;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    border: none;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
    border-radius: 8px;
    border: 1px solid transparent;
    background-clip: content-box;
    -webkit-background-clip: content-box;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2563eb 0%, #dc2626 100%);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    transform: scale(1.05);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #60a5fa 0%, #f87171 100%);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
  }

  /* Modal scrollbar */
  .modal-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    -webkit-appearance: none;
  }

  .modal-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 10px;
    border: none;
  }

  .modal-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: content-box;
    -webkit-background-clip: content-box;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .modal-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2563eb 0%, #dc2626 100%);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    transform: scale(1.1);
  }

  .dark .modal-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.03);
  }

  .dark .modal-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #60a5fa 0%, #f87171 100%);
  }

  .dark .modal-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
  }

  /* Force scrollbar styling in Safari */
  @media screen and (-webkit-min-device-pixel-ratio: 0) {
    ::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 8px;
      height: 8px;
    }
    
    ::-webkit-scrollbar-thumb {
      -webkit-appearance: none;
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%);
      border-radius: 10px;
      border: 2px solid transparent;
      background-clip: content-box;
      -webkit-background-clip: content-box;
    }
    
    .custom-scrollbar::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 6px;
      height: 6px;
    }
    
    .modal-scrollbar::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 8px;
      height: 8px;
    }
  }

  /* Additional Safari-specific rules */
  @supports (-webkit-touch-callout: none) {
    ::-webkit-scrollbar {
      width: 8px !important;
      height: 8px !important;
      -webkit-appearance: none !important;
    }
    
    ::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%) !important;
      border-radius: 10px !important;
      border: 2px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
      -webkit-appearance: none !important;
    }
    
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px !important;
      height: 6px !important;
      -webkit-appearance: none !important;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%) !important;
      border-radius: 8px !important;
      border: 1px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
    }
    
    .modal-scrollbar::-webkit-scrollbar {
      width: 8px !important;
      height: 8px !important;
      -webkit-appearance: none !important;
    }
    
    .modal-scrollbar::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6 0%, #ef4444 100%) !important;
      border-radius: 10px !important;
      border: 2px solid transparent !important;
      background-clip: content-box !important;
      -webkit-background-clip: content-box !important;
    }
  }

  /* Party-specific scrollbar styles */
  .democrat-scrollbar::-webkit-scrollbar {
    width: 6px !important;
    height: 6px !important;
    -webkit-appearance: none !important;
  }

  .democrat-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05) !important;
    border-radius: 8px !important;
    border: none !important;
  }

  .democrat-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%) !important;
    border-radius: 8px !important;
    border: 1px solid transparent !important;
    background-clip: content-box !important;
    -webkit-background-clip: content-box !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
    transition: all 0.3s ease !important;
  }

  .democrat-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%) !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2) !important;
    transform: scale(1.05) !important;
  }

  .dark .democrat-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05) !important;
  }

  .dark .democrat-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%) !important;
  }

  .dark .democrat-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  }

  .republican-scrollbar::-webkit-scrollbar {
    width: 6px !important;
    height: 6px !important;
    -webkit-appearance: none !important;
  }

  .republican-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05) !important;
    border-radius: 8px !important;
    border: none !important;
  }

  .republican-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%) !important;
    border-radius: 8px !important;
    border: 1px solid transparent !important;
    background-clip: content-box !important;
    -webkit-background-clip: content-box !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
    transition: all 0.3s ease !important;
  }

  .republican-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%) !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2) !important;
    transform: scale(1.05) !important;
  }

  .dark .republican-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05) !important;
  }

  .dark .republican-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #f87171 0%, #ef4444 100%) !important;
  }

  .dark .republican-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  }

  /* Firefox scrollbar for party-specific elements */
  .democrat-scrollbar {
    scrollbar-width: thin !important;
    scrollbar-color: #3b82f6 transparent !important;
  }

  .dark .democrat-scrollbar {
    scrollbar-color: #60a5fa transparent !important;
  }

  .republican-scrollbar {
    scrollbar-width: thin !important;
    scrollbar-color: #ef4444 transparent !important;
  }

  .dark .republican-scrollbar {
    scrollbar-color: #f87171 transparent !important;
  }
`;
document.head.appendChild(fontStyle);

const CivicDutyApp = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentView, setCurrentView] = useState('home');
  const [selectedPolitician, setSelectedPolitician] = useState(null);
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedHomepageTag, setSelectedHomepageTag] = useState('all');
  const [expandedUpdate, setExpandedUpdate] = useState(null);
  const [politicians, setPoliticians] = useState([]);
  const [politicianUpdates, setPoliticianUpdates] = useState({});
  const [policyTags, setPolicyTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [members, setMembers] = useState([]);
  const [showPressReleases, setShowPressReleases] = useState(false);
  const [pressReleases, setPressReleases] = useState([]);
  const [pressLoading, setPressLoading] = useState(false);
  const [pressError, setPressError] = useState(null);
  const [pressFilter, setPressFilter] = useState('all');
  const [showPressModal, setShowPressModal] = useState(false);
  const [selectedPressRelease, setSelectedPressRelease] = useState(null);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagLoading, setTagLoading] = useState(false);
  const [tagError, setTagError] = useState(null);
  const [showTagEditModal, setShowTagEditModal] = useState(false);
  const [editingTags, setEditingTags] = useState([]);
  const [tagEditLoading, setTagEditLoading] = useState(false);
  const [tagEditError, setTagEditError] = useState(null);
  const [showTagDeleteModal, setShowTagDeleteModal] = useState(false);
  const [deletingTag, setDeletingTag] = useState(null);
  const [tagDeleteLoading, setTagDeleteLoading] = useState(false);
  const [tagDeleteError, setTagDeleteError] = useState(null);
  const [showTagAddModal, setShowTagAddModal] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tagAddLoading, setTagAddLoading] = useState(false);
  const [tagAddError, setTagAddError] = useState(null);
  const [showTagEditPressModal, setShowTagEditPressModal] = useState(false);
  const [editingPressTags, setEditingPressTags] = useState([]);
  const [tagEditPressLoading, setTagEditPressLoading] = useState(false);
  const [tagEditPressError, setTagEditPressError] = useState(null);
  const [showTagDeletePressModal, setShowTagDeletePressModal] = useState(false);
  const [deletingPressTag, setDeletingPressTag] = useState(null);
  const [tagDeletePressLoading, setTagDeletePressLoading] = useState(false);
  const [tagDeletePressError, setTagDeletePressError] = useState(null);
  const [showTagAddPressModal, setShowTagAddPressModal] = useState(false);
  const [newPressTag, setNewPressTag] = useState('');
  const [tagAddPressLoading, setTagAddPressLoading] = useState(false);
  const [tagAddPressError, setTagAddPressError] = useState(null);
  const [showTagEditPressReleaseModal, setShowTagEditPressReleaseModal] = useState(false);
  const [editingPressReleaseTags, setEditingPressReleaseTags] = useState([]);
  const [tagEditPressReleaseLoading, setTagEditPressReleaseLoading] = useState(false);
  const [tagEditPressReleaseError, setTagEditPressReleaseError] = useState(null);
  const [showTagDeletePressReleaseModal, setShowTagDeletePressReleaseModal] = useState(false);
  const [deletingPressReleaseTag, setDeletingPressReleaseTag] = useState(null);
  const [tagDeletePressReleaseLoading, setTagDeletePressReleaseLoading] = useState(false);
  const [tagDeletePressReleaseError, setTagDeletePressReleaseError] = useState(null);
  const [showTagAddPressReleaseModal, setShowTagAddPressReleaseModal] = useState(false);
  const [newPressReleaseTag, setNewPressReleaseTag] = useState('');
  const [tagAddPressReleaseLoading, setTagAddPressReleaseLoading] = useState(false);
  const [tagAddPressReleaseError, setTagAddPressReleaseError] = useState(null);
  const [showTagEditPressReleaseTagModal, setShowTagEditPressReleaseTagModal] = useState(false);
  const [editingPressReleaseTagTags, setEditingPressReleaseTagTags] = useState([]);
  const [tagEditPressReleaseTagLoading, setTagEditPressReleaseTagLoading] = useState(false);
  const [tagEditPressReleaseTagError, setTagEditPressReleaseTagError] = useState(null);
  const [showTagDeletePressReleaseTagModal, setShowTagDeletePressReleaseTagModal] = useState(false);
  const [deletingPressReleaseTagTag, setDeletingPressReleaseTagTag] = useState(null);
  const [tagDeletePressReleaseTagLoading, setTagDeletePressReleaseTagLoading] = useState(false);
  const [tagDeletePressReleaseTagError, setTagDeletePressReleaseTagError] = useState(null);
  const [showTagAddPressReleaseTagModal, setShowTagAddPressReleaseTagModal] = useState(false);
  const [newPressReleaseTagTag, setNewPressReleaseTagTag] = useState('');
  const [tagAddPressReleaseTagLoading, setTagAddPressReleaseTagLoading] = useState(false);
  const [tagAddPressReleaseTagError, setTagAddPressReleaseTagError] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    fetchPoliticians();
    fetchPolicyTags();
  }, []);

  useEffect(() => {
    if (politicians.length > 0) {
      fetchAllPoliticianUpdates();
    }
  }, [politicians]);

  useEffect(() => {
    if (selectedPolitician && !politicianUpdates[selectedPolitician.id]) {
      fetchPoliticianUpdates(selectedPolitician.id);
    }
  }, [selectedPolitician]);

  const fetchPolicyTags = async () => {
    try {
      setTagLoading(true);
              const response = await fetch(buildApiUrl('/api/policy-tags'));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const allTags = await response.json();
      // Filter for tags with scope "universal" only
      const universalTags = allTags.filter(tag => tag.scope === 'universal');
      console.log('📋 Loaded universal policy tags from database:', universalTags.map(t => t.name));
      setAvailableTags(universalTags);
    } catch (err) {
      console.error('❌ Error fetching policy tags:', err);
      setAvailableTags([]);
    } finally {
      setTagLoading(false);
    }
  };

  const fetchPoliticians = async () => {
    try {
      setLoading(true);
      setError(null);
      
              const response = await fetch(buildApiUrl('/api/politicians'));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📊 Loaded politicians from MongoDB:', data);
      setPoliticians(data);
    } catch (err) {
      console.error('❌ Error fetching politicians:', err);
      setError('Failed to load politicians. Please check your internet connection and try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPoliticianUpdates = async (politicianId, skipLoading = false) => {
    try {
      if (!skipLoading) {
        setUpdatesLoading(true);
      }
      
              const response = await fetch(buildApiUrl(`/api/politicians/${politicianId}/updates`));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const updates = await response.json();
      console.log(`📰 Loaded ${updates.length} approved updates for politician ${politicianId}:`, updates);
      
      setPoliticianUpdates(prev => ({
        ...prev,
        [politicianId]: updates
      }));
    } catch (err) {
      console.error('❌ Error fetching politician updates:', err);
      setPoliticianUpdates(prev => ({
        ...prev,
        [politicianId]: []
      }));
    } finally {
      if (!skipLoading) {
        setUpdatesLoading(false);
      }
    }
  };

  const fetchAllPoliticianUpdates = async () => {
    try {
      console.log('🔄 Fetching updates for all politicians...');
      const updatePromises = politicians.map(politician => 
        fetchPoliticianUpdates(politician.id, true) // Use 'id' instead of '_id'
      );
      
      await Promise.all(updatePromises);
      console.log('✅ Finished fetching updates for all politicians');
      console.log('📊 Current politicianUpdates state:', Object.keys(politicianUpdates).map(id => ({
        politicianId: id,
        updateCount: politicianUpdates[id]?.length || 0
      })));
    } catch (error) {
      console.error('❌ Error fetching all politician updates:', error);
    }
  };

  const getPartyColor = (party) => {
    if (!party) return isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    return party.toLowerCase() === 'republican' 
      ? isDarkMode ? 'bg-gradient-to-r from-red-500 to-red-700 text-white' : 'bg-gradient-to-r from-red-400 to-red-600 text-white'
      : party.toLowerCase() === 'democrat'
        ? isDarkMode ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white' : 'bg-gradient-to-r from-blue-400 to-blue-600 text-white'
        : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
  };

  const getPartyGradientBg = (party) => {
    if (!party) return isDarkMode ? 'bg-gray-700' : 'bg-gray-100';
    return party.toLowerCase() === 'republican' 
      ? isDarkMode ? 'bg-gradient-to-br from-red-500 to-red-700' : 'bg-gradient-to-br from-red-400 to-red-600'
      : party.toLowerCase() === 'democrat'
        ? isDarkMode ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 'bg-gradient-to-br from-blue-400 to-blue-600'
        : isDarkMode ? 'bg-gray-700' : 'bg-gray-100';
  };

  const getPartyButtonClass = (party, isSelected = false) => {
    if (!party) return isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200';
    
    if (isSelected) {
      return party.toLowerCase() === 'republican' 
        ? 'bg-gradient-to-r from-red-500 to-red-700 text-white shadow-md'
        : party.toLowerCase() === 'democrat'
        ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-md'
        : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
    
    return isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200';
  };

  const getPartyBorderClass = (party) => {
    if (!party) return 'border-gray-500';
    return party.toLowerCase() === 'republican' 
      ? 'border-red-500'
      : party.toLowerCase() === 'democrat'
        ? 'border-blue-500'
        : 'border-gray-500';
  };

  const getPartyAccentClass = (party) => {
    if (!party) return isDarkMode ? 'text-gray-400' : 'text-gray-600';
    return party.toLowerCase() === 'republican' 
      ? isDarkMode ? 'text-red-400' : 'text-red-600'
      : party.toLowerCase() === 'democrat'
        ? isDarkMode ? 'text-blue-400' : 'text-blue-600'
        : isDarkMode ? 'text-gray-400' : 'text-gray-600';
  };

  const getPartyWebsiteButtonClass = (party) => {
    if (!party) return 'bg-gray-500 hover:bg-gray-600';
    return party.toLowerCase() === 'republican' 
      ? 'bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800'
      : party.toLowerCase() === 'democrat'
        ? 'bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800'
        : 'bg-gray-500 hover:bg-gray-600';
  };

  const getPartyProfileBg = (party) => {
    if (!party) return isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50';
    return party.toLowerCase() === 'republican' 
      ? isDarkMode ? 'bg-gradient-to-br from-red-900/30 to-red-800/20' : 'bg-gradient-to-br from-red-50 via-white to-red-50'
      : party.toLowerCase() === 'democrat'
        ? isDarkMode ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/20' : 'bg-gradient-to-br from-blue-50 via-white to-blue-50'
        : isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50';
  };

  const getTagColor = (tag) => {
    const colors = {
      'Economy': isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800',
      'Education': isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800',
      'Healthcare': isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800',
      'Taxes & Government Spending': isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800',
      'Crime & Justice': isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800',
      'Firearm Policy': isDarkMode ? 'bg-pink-900/50 text-pink-300' : 'bg-pink-100 text-pink-800',
      'Foreign Policy & Defense': isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
      'Environment & Climate': isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-800',
      'Energy': isDarkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-800',
      'Disability': isDarkMode ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-100 text-cyan-800',
      'Family & Social Issues': isDarkMode ? 'bg-lime-900/50 text-lime-300' : 'bg-lime-100 text-lime-800',
      'Immigration & Border Security': isDarkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-800',
      'Agriculture Policy': isDarkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-100 text-violet-800',
      'Technology & Privacy': isDarkMode ? 'bg-fuchsia-900/50 text-fuchsia-300' : 'bg-fuchsia-100 text-fuchsia-800',
      'US Economy & Jobs': isDarkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      'Other': isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
    };
    return colors[tag] || (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const [year, month, day] = dateString.split('-').map(Number);
    return `${months[month - 1]} ${day}, ${year}`;
  };

  const hasUpdatesForTag = (politicianId, tag) => {
    try {
      const updates = politicianUpdates[politicianId] || [];
      console.log(`🔍 Checking updates for politician ${politicianId}, tag: ${tag}, updates count: ${updates.length}`);
      if (tag === 'all') {
        const hasUpdates = updates.length > 0;
        console.log(`🔍 All topics selected, has updates: ${hasUpdates}`);
        return hasUpdates;
      }
      const hasTagUpdates = updates.some(update => update.tags && Array.isArray(update.tags) && update.tags.includes(tag));
      console.log(`🔍 Tag ${tag} selected, has updates: ${hasTagUpdates}`);
      return hasTagUpdates;
    } catch (error) {
      console.error('Error in hasUpdatesForTag:', error, { politicianId, tag });
      return false;
    }
  };

  const handleHomepageTagSelect = (tagName) => {
    try {
      console.log('Selecting homepage tag:', tagName);
      setSelectedHomepageTag(tagName);
    } catch (error) {
      console.error('Error in handleHomepageTagSelect:', error);
    }
  };

  const filteredPoliticians = politicians.filter(politician => {
    try {
      if (!politician) return false;
      
      const matchesSearch = 
        (politician.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (politician.office || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (politician.location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (politician.party || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesParty = !selectedParty || politician.party === selectedParty;
      
      const matchesHomepageTag = selectedHomepageTag === 'all' || hasUpdatesForTag(politician.id, selectedHomepageTag);
      
      return matchesSearch && matchesParty && matchesHomepageTag;
    } catch (error) {
      console.error('Error filtering politician:', error, { politician });
      return false;
    }
  });

  const currentUpdates = selectedPolitician ? (politicianUpdates[selectedPolitician.id] || [])
    .filter(update => {
      if (selectedTags.length === 0) return true;
      return update.tags && selectedTags.some(tag => update.tags.includes(tag));
    }) : [];

  const handlePoliticianClick = (politician) => {
    setSelectedPolitician(politician);
    setCurrentView('profile');
    setSelectedTag(selectedHomepageTag === 'all' ? 'all' : selectedHomepageTag);
    setSelectedTags(selectedHomepageTag === 'all' ? [] : [selectedHomepageTag]);
    setExpandedUpdate(null);
    setUpdatesLoading(false);
  };

  const handleBackToHome = () => {
    setCurrentView('home');
    setShowAbout(false);
    setSelectedPolitician(null);
    setExpandedUpdate(null);
    setSelectedTag('all');
  };

  const handleBillsClick = () => {
    setCurrentView('bills');
  };

  const handleUpdateClick = (update, e) => {
    e.stopPropagation();
    setExpandedUpdate({
      ...update,
      date: update.formattedDate
    });
  };

  const handleCloseModal = () => {
    setExpandedUpdate(null);
  };

  const handleTagToggle = (tagName) => {
    setSelectedTags(prev => {
      if (prev.includes(tagName)) {
        return [];
      }
      return [tagName];
    });
  };

  const getTagCounts = (politicianId) => {
    const updates = politicianUpdates[politicianId] || [];
    const counts = {};
    
    availableTags.forEach(tag => {
      counts[tag.name] = 0;
    });
    
    updates.forEach(update => {
      if (update.tags && Array.isArray(update.tags)) {
        update.tags.forEach(tag => {
          if (counts[tag] !== undefined) {
            counts[tag]++;
          }
        });
      }
    });
    
    return counts;
  };

  const getLatestActivityForTag = (politicianId, tag) => {
    try {
      const updates = politicianUpdates[politicianId] || [];
      if (tag === 'all') {
        return updates[0] || null;
      }
      return updates.find(update => update.tags && Array.isArray(update.tags) && update.tags.includes(tag)) || null;
    } catch (error) {
      console.error('Error in getLatestActivityForTag:', error, { politicianId, tag });
      return null;
    }
  };

  // Function to get the latest update date for a politician
  const getLatestUpdateDate = (politicianId, tag) => {
    try {
      const latestActivity = getLatestActivityForTag(politicianId, tag);
      if (!latestActivity || !latestActivity.date) {
        return new Date(0); // Return epoch date for politicians with no updates
      }
      return new Date(latestActivity.date);
    } catch (error) {
      console.error('Error in getLatestUpdateDate:', error, { politicianId, tag });
      return new Date(0);
    }
  };

  // Function to sort politicians by latest update date
  const sortPoliticiansByLatestUpdate = (politicians, tag) => {
    return [...politicians].sort((a, b) => {
      const dateA = getLatestUpdateDate(a.id, tag);
      const dateB = getLatestUpdateDate(b.id, tag);
      return dateB - dateA; // Sort in descending order (newest first)
    });
  };

  if (showAbout) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <header className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent">
                  CivicDuty
                </h1>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  aria-label="Toggle dark mode"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5" />
                  ) : (
                    <Moon className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={handleBackToHome}
                  className={`flex items-center px-4 py-2 ${getPartyButtonClass(selectedPolitician.party, true)} rounded-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-x-1`}
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  <span>Back to Home</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-8 border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <div className="text-center mb-12">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gradient-to-r from-red-100 to-blue-100'} mb-6`}>
                <Users className={`w-8 h-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              </div>
              <h1 className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Our Mission</h1>
              <p className={`text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} max-w-2xl mx-auto`}>
                Empowering citizens with transparent access to their representatives' activities and policy positions
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-red-50 to-blue-50'} rounded-xl p-6`}>
                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Transparency</h2>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  We believe in making government more accessible and transparent. Our platform provides easy access to your representatives' activities, policy positions, and legislative updates.
                </p>
              </div>
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-blue-50 to-red-50'} rounded-xl p-6`}>
                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Engagement</h2>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  Stay informed about the decisions that affect your community. Track press releases, policy changes, and legislative activities in real-time.
                </p>
              </div>
            </div>

            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-8 mb-12`}>
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-6 text-center`}>What We Offer</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className={`w-12 h-12 ${isDarkMode ? 'bg-blue-900' : 'bg-blue-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <Search className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Easy Search</h3>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Find your representatives quickly with our intuitive search interface</p>
                </div>
                <div className="text-center">
                  <div className={`w-12 h-12 ${isDarkMode ? 'bg-red-900' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <Newspaper className={`w-6 h-6 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                  </div>
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Press Updates</h3>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Stay updated with the latest press releases and announcements</p>
                </div>
                <div className="text-center">
                  <div className={`w-12 h-12 ${isDarkMode ? 'bg-purple-900' : 'bg-purple-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <Tag className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Policy Tags</h3>
                  <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Track policy positions and legislative activities by topic</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={handleBackToHome}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-blue-600 text-white font-semibold rounded-lg hover:from-red-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Return to Homepage
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading Georgia representatives and policy tags...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchPoliticians}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (currentView === 'bills') {
    return (
      <Bills 
        onBackToHome={handleBackToHome} 
        isDarkMode={isDarkMode} 
        setIsDarkMode={setIsDarkMode} 
      />
    );
  }

  if (currentView === 'profile' && selectedPolitician) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <header className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBackToHome}
                  className={`flex items-center px-4 py-2 ${getPartyButtonClass(selectedPolitician.party, true)} rounded-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-x-1`}
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  <span>Back to Home</span>
                </button>
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent">
                    CivicDuty
                  </h1>
                </div>
              </div>
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </header>

        <div className="flex h-[calc(100vh-80px)]">
          <div className={`w-2/5 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} flex flex-col`}>
            <div className={`flex-shrink-0 ${isDarkMode ? 'bg-gray-900' : getPartyProfileBg(selectedPolitician.party)} px-8 py-10 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-50'}`}>
              <div className="text-center">
                <div className="relative inline-block mb-6">
                  <img
                    src={selectedPolitician.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedPolitician.name)}&background=random&color=fff`}
                    alt={selectedPolitician.name}
                    className={`w-24 h-24 rounded-full object-cover border-4 mb-3 ${getPartyBorderClass(selectedPolitician.party)}`}
                  />
                </div>
                <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2 leading-tight`}>
                  {selectedPolitician.name}
                </h1>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} font-medium mb-3`}>{selectedPolitician.office}</p>
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${getPartyColor(selectedPolitician.party)} inline-block`}>
                  {selectedPolitician.party || 'Independent'}
                </span>
              </div>
            </div>

            <div className={`flex-1 px-8 py-6 space-y-6 overflow-y-auto ${
              selectedPolitician.party === 'Democrat' 
                ? 'democrat-scrollbar' 
                : selectedPolitician.party === 'Republican'
                ? 'republican-scrollbar'
                : 'custom-scrollbar'
            }`}>
              <div className="grid grid-cols-1 gap-4">
                <div className={`flex items-center space-x-3 p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl`}>
                  <div className={`w-10 h-10 ${getPartyGradientBg(selectedPolitician.party)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Represents</p>
                    <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} truncate`}>{selectedPolitician.location}</p>
                  </div>
                </div>

                <div className={`flex items-center space-x-3 p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl`}>
                  <div className={`w-10 h-10 ${getPartyGradientBg(selectedPolitician.party)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Current Term</p>
                    <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedPolitician.termStart && selectedPolitician.termEnd 
                        ? `${formatDate(selectedPolitician.termStart)} - Present`
                        : 'Term information unavailable'
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gradient-to-r from-blue-50 to-purple-50'} rounded-xl p-6 border ${isDarkMode ? 'border-gray-600' : 'border-blue-100'}`}>
                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3 uppercase tracking-wide`}>About</h3>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                  {selectedPolitician.description || 'No biography available for this representative.'}
                </p>
              </div>
            </div>

            {selectedPolitician.website && selectedPolitician.website !== '#' && (
              <div className="px-8 pb-6">
                <a
                  href={selectedPolitician.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full inline-flex items-center justify-center px-6 py-3 text-white font-medium rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${getPartyWebsiteButtonClass(selectedPolitician.party)}`}
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Visit Official Website
                </a>
              </div>
            )}

          </div>

          <div className={`w-3/5 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex flex-col`}>
            <div className={`flex-shrink-0 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} px-8 py-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Updates</h2>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Latest activity and policy positions</p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="relative flex items-center">
                    <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg ring-2 ring-green-400"></span>
                  </div>
                  <span className="text-sm font-bold text-green-700 tracking-wide uppercase drop-shadow-sm animate-pulse">
                    Live
                  </span>
                </div>
              </div>
              
              <div className={`flex flex-wrap gap-2 max-h-32 overflow-y-auto ${
                selectedPolitician.party === 'Democrat'
                  ? 'democrat-scrollbar'
                  : selectedPolitician.party === 'Republican'
                  ? 'republican-scrollbar'
                  : 'custom-scrollbar'
              }`}>
                <button
                  onClick={() => setSelectedTags([])}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedTags.length === 0
                      ? getPartyButtonClass(selectedPolitician.party, true)
                      : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  } flex items-center gap-2`}
                >
                  All
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
                    selectedTags.length === 0
                      ? 'bg-white text-gray-800'
                      : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                  }`}>
                    {(politicianUpdates[selectedPolitician.id] || []).length}
                  </span>
                </button>
                {availableTags.map(tag => {
                  const tagCounts = getTagCounts(selectedPolitician.id);
                  const count = tagCounts[tag.name] || 0;
                  return (
                    <button
                      key={tag.id || tag._id}
                      onClick={() => handleTagToggle(tag.name)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        selectedTags.includes(tag.name)
                          ? getPartyButtonClass(selectedPolitician.party, true)
                          : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                      } flex items-center gap-2`}
                    >
                      {tag.name}
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
                        selectedTags.includes(tag.name)
                          ? 'bg-white text-gray-800'
                          : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={`flex-1 overflow-y-auto px-8 py-6 ${
              selectedPolitician.party === 'Democrat'
                ? 'democrat-scrollbar'
                : selectedPolitician.party === 'Republican'
                ? 'republican-scrollbar'
                : 'custom-scrollbar'
            }`}>
              {updatesLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading updates...</p>
                  </div>
                </div>
              ) : currentUpdates.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <Bell className={`w-12 h-12 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
                    <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>No updates found</h3>
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      {selectedTags.length === 0 ? 'No approved updates are available yet.' : `No updates found for the selected tags.`}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {currentUpdates.map((update) => (
                    <div 
                      key={update.id} 
                      onClick={(e) => handleUpdateClick(update, e)}
                      className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 border ${
                        selectedPolitician.party === 'Republican'
                          ? isDarkMode ? 'border-gray-800 hover:border-red-800/50' : 'hover:border-red-200'
                          : isDarkMode ? 'border-gray-800 hover:border-blue-800/50' : 'hover:border-blue-200'
                      } hover:shadow-lg transition-all duration-200 cursor-pointer group`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className={`flex items-center space-x-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <Calendar className="w-4 h-4" />
                          <span>{update.formattedDate}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 justify-end">
                          {update.tags && update.tags.map((tag, index) => (
                            <span key={index} className={`px-3 py-1 rounded-full text-xs font-medium ${getTagColor(tag)}`}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'} leading-relaxed font-medium ${
                        selectedPolitician.party === 'Republican'
                          ? isDarkMode ? 'group-hover:text-red-300' : 'group-hover:text-red-900'
                          : isDarkMode ? 'group-hover:text-blue-300' : 'group-hover:text-blue-900'
                      } transition-colors`}>
                        {update.summary}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {expandedUpdate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm animate-in fade-in duration-300">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto modal-scrollbar shadow-2xl transform transition-all duration-300 scale-100 animate-in slide-in-from-bottom-4`}>
              <div className={`sticky top-0 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} p-6 rounded-t-2xl`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 pr-4">
                    <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Update Details</h2>
                  </div>
                  <button
                    onClick={handleCloseModal}
                    className={`${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors p-2 hover:bg-gray-100 rounded-full`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className={`flex items-center space-x-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-6`}>
                  <Calendar className="w-4 h-4" />
                  <span>{expandedUpdate.formattedDate}</span>
                </div>
                
                <div className="prose prose-lg max-w-none">
                  <p className={`${isDarkMode ? 'text-gray-300' : 'text-black'} leading-relaxed text-lg mb-6 font-semibold`}>
                    {expandedUpdate.fullSummary}
                  </p>
                </div>

                {(() => { 
                  let tagsToShow = Array.isArray(expandedUpdate.tags) && expandedUpdate.tags.length > 0
                    ? expandedUpdate.tags.filter(Boolean)
                    : (expandedUpdate.category ? [expandedUpdate.category] : []);
                  return tagsToShow.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mt-2 mb-6">
                      {tagsToShow.map((tag, idx) => (
                        <span key={idx} className={`px-3 py-1 rounded-full text-xs font-medium ${getTagColor(tag)}`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null;
                })()}

                <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} pt-6`}>
                  {expandedUpdate.pressReleaseUrl && expandedUpdate.pressReleaseUrl !== '#' ? (
                    <a
                      href={expandedUpdate.pressReleaseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-blue-600 text-white font-medium rounded-xl hover:from-red-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Read Full Press Release
                    </a>
                  ) : (
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} italic`}>Original press release not available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <header className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent">
                CivicDuty
              </h1>
            </div>
            
            {/* Search bar centered in header */}
            <div className="flex-1 max-w-md mx-8">
              <div className="relative">
                <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'} w-5 h-5`} />
                <input
                  type="text"
                  placeholder="Search by name, office, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-12 pr-6 py-3 text-base border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={handleBillsClick}
                className={`flex items-center px-4 py-2 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-lg transition-all shadow-sm hover:shadow-md transform hover:scale-105`}
              >
                <FileText className="w-4 h-4 mr-2" />
                View Bills
              </button>
            </div>
          </div>
        </div>
      </header>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <div className="text-center mb-4">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                Filter by Policy Topics
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Select a topic to see representatives with recent activity in that area
              </p>
            </div>
            
            {/* Tag filtering buttons */}
            <div className="flex flex-wrap justify-center gap-2 max-h-32 overflow-y-auto custom-scrollbar">
              <button
                onClick={() => handleHomepageTagSelect('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  selectedHomepageTag === 'all'
                    ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                All Topics
              </button>
              {availableTags.map(tag => (
                <button
                  key={tag.id || tag._id}
                  onClick={() => handleHomepageTagSelect(tag.name)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedHomepageTag === tag.name
                      ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-md'
                      : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPoliticians.length === 0 ? (
            <div className="text-center py-12">
              <div className={`w-16 h-16 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                <Users className={`w-8 h-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <h3 className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} mb-2`}>
                No representatives found
              </h3>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                {selectedHomepageTag === 'all' 
                  ? 'No representatives match your search criteria.' 
                  : `No representatives have recent activity in "${selectedHomepageTag}".`
                }
              </p>
              <button
                onClick={() => {
                  setSelectedHomepageTag('all');
                  setSearchTerm('');
                }}
                className="mt-4 px-4 py-2 bg-gradient-to-r from-red-600 to-blue-600 text-white rounded-lg hover:from-red-700 hover:to-blue-700 transition-all"
              >
                Show All Representatives
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortPoliticiansByLatestUpdate(filteredPoliticians, selectedHomepageTag).map((politician) => (
                <div
                  key={politician.id}
                  onClick={() => handlePoliticianClick(politician)}
                  className={`group relative ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer ${
                    politician.party === 'Democrat'
                      ? isDarkMode ? 'hover:border-blue-800' : 'hover:border-blue-200'
                      : politician.party === 'Republican'
                      ? isDarkMode ? 'hover:border-red-800/50' : 'hover:border-red-200'
                      : isDarkMode ? 'hover:border-gray-600' : 'hover:border-gray-200'
                  } border-2 border-transparent`}
                >
                  <div className="p-6">
                    <div className="flex flex-col items-center text-center">
                      <img
                        src={politician.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(politician.name)}&background=random&color=fff`}
                        alt={politician.name}
                        className={`w-24 h-24 rounded-full object-cover border-4 mb-3 ${getPartyBorderClass(politician.party)}`}
                      />
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mb-4 ${getPartyColor(politician.party)}`}>
                        {politician.party}
                      </span>
                      <h3 className={`text-lg font-semibold mb-2 ${getPartyAccentClass(politician.party)}`}>
                        {politician.name}
                      </h3>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        {politician.office}
                      </p>
                      <div className="flex items-center justify-center text-sm mb-4">
                        <MapPin className={`w-4 h-4 mr-1 ${getPartyAccentClass(politician.party)}`} />
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                          {politician.location}
                        </span>
                      </div>
                    </div>

                    <div className={`mt-4 p-4 rounded-lg border transition-colors ${
                      politician.party === 'Democrat' 
                        ? isDarkMode ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-800 hover:from-blue-900/50 hover:to-blue-800/30' : 'bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-100 hover:from-blue-100 hover:to-blue-200/50'
                        : politician.party === 'Republican'
                        ? isDarkMode ? 'bg-gradient-to-br from-red-900/30 to-red-800/20 border-red-800 hover:from-red-900/50 hover:to-red-800/30' : 'bg-gradient-to-br from-red-50 to-red-100/50 border-red-100 hover:from-red-100 hover:to-red-200/50'
                        : isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
                    }`}>
                      <div className={`flex items-center justify-between text-sm font-medium mb-2 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <div className="flex items-center">
                          <TrendingUp className={`w-4 h-4 mr-2 ${getPartyAccentClass(politician.party)}`} />
                          Latest Activity
                        </div>
                        {(() => {
                          const latestActivity = getLatestActivityForTag(politician.id, selectedHomepageTag);
                          if (latestActivity) {
                            return (
                              <div className={`flex items-center text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <Calendar className="w-3 h-3 mr-1" />
                                <span>{latestActivity.formattedDate}</span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      {(() => {
                        const latestActivity = getLatestActivityForTag(politician.id, selectedHomepageTag);
                        if (latestActivity) {
                          return (
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} line-clamp-3`}>
                              {latestActivity.summary}
                            </p>
                          );
                        } else {
                          return (
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {selectedHomepageTag === 'all' ? 'No recent activity' : `No recent activity in ${selectedHomepageTag}`}
                            </p>
                          );
                        }
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CivicDutyApp;

